/**
 * Canonical consent vocabulary. Every consent status, category, and storage
 * attribute used by this module comes from here — there are no consent string
 * literals at call sites or inside `consent.js` / `cookie.js`. Keeping the
 * storage contract in one frozen place is what lets the module be vendored
 * byte-identical across agapae.gr, app.agapae.gr, and invite.agapae.gr without
 * the three copies drifting apart.
 *
 * These values ARE the storage contract: the cookie they describe is already
 * live on real visitors' devices (written by the cookie banner since launch),
 * and it is shared across all three subdomains. Changing a value here silently
 * invalidates every stored consent record and re-prompts every visitor — see
 * docs/analytics/consent-foundation.md before touching one.
 */

/**
 * Name of the first-party cookie holding the visitor's consent record.
 * Unchanged from the pre-refactor banner implementation.
 * @readonly
 */
export const CONSENT_COOKIE_NAME = "agapae-consent";

/**
 * How long a stored consent record survives, in days. A year is the common
 * practice for consent records under Greek Law 3471/2006 / GDPR and matches
 * what the Cookie Policy tells visitors.
 * @readonly
 */
export const CONSENT_COOKIE_MAX_AGE_DAYS = 365;

/** Cookie path — site-wide, so every route on a surface sees the same record. @readonly */
export const CONSENT_COOKIE_PATH = "/";

/**
 * `SameSite=Lax` — the record must survive a top-level navigation from an ad or
 * a search result, but never ride a cross-site subrequest.
 * @readonly
 */
export const CONSENT_COOKIE_SAME_SITE = "Lax";

/**
 * The registrable domain the three surfaces share. When the current hostname
 * matches, the cookie is scoped to `CONSENT_COOKIE_DOMAIN` so one answer covers
 * agapae.gr, app.agapae.gr, and invite.agapae.gr. On localhost (ports, not
 * subdomains) no domain attribute is set — see `cookie.js`.
 * @readonly
 */
export const CONSENT_COOKIE_DOMAIN_SUFFIX = "agapae.gr";

/** The `domain=` attribute value used on production hosts. @readonly */
export const CONSENT_COOKIE_DOMAIN = ".agapae.gr";

/**
 * Consent lifecycle states.
 *
 * `UNKNOWN` is never persisted — it is the EFFECTIVE status of a visitor who
 * has not answered the banner yet, and the reason `getConsent()` can always
 * return a usable state while `getStoredConsent()` still reports `null`.
 * @readonly
 */
export const ConsentStatus = Object.freeze({
  UNKNOWN: "unknown",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
});

/**
 * The consent categories the banner records. These are the keys of a
 * `ConsentPreferences` object and the argument accepted by `hasConsent()`.
 *
 * `ESSENTIAL` is always granted: under Article 4(5) of Greek Law 3471/2006
 * strictly necessary storage does not require consent. `ANALYTICS` and
 * `MARKETING` are the two non-essential categories — nothing gates on them yet
 * (see docs/analytics/consent-foundation.md, "Future extension points").
 * @readonly
 */
export const ConsentCategories = Object.freeze({
  ESSENTIAL: "essential",
  ANALYTICS: "analytics",
  MARKETING: "marketing",
});

/**
 * Deny-by-default preferences: the state assumed for a visitor who has not
 * answered the banner. Non-essential categories start `false` and only a
 * deliberate act turns them on — the default direction GDPR requires, and the
 * default the banner already used before this module existed.
 *
 * Frozen, and never handed out directly: `getDefaultConsent()` returns a fresh
 * copy so a consumer can never mutate the shared default.
 * @readonly
 */
export const DEFAULT_CONSENT_PREFERENCES = Object.freeze({
  essential: true,
  analytics: false,
  marketing: false,
});
